import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import useStore from '../../../../store/storeContext';
import { DocumentDto } from '../../../../types/document';
import { OrganizationDto } from '../../../../types/organization';

interface Props {
    isOpen: boolean;
    close(): void;
    type?: 'LARGE_REPORT' | 'YEARLY_REPORT';
}

function CrossOrganizationDialog(props: Props) {
    const { document, localization, loadingIndicator, snackbar, theming } = useStore();

    const [docs, setDocs] = useState<(DocumentDto | null)[]>([]);
    const [orgs, setOrgs] = useState<OrganizationDto[]>([] as OrganizationDto[]);

    useEffect(() => {
        props.isOpen && init();
    }, [props.isOpen]);

    async function init() {
        setDocs([]);
        try {
            loadingIndicator.show();
            const response = await document.fetchOfficialDocuments(props.type || null);
            const documents = response.documents;
            const organizations = response.organizations;
            const organizationsInAlphabeticalOrder = organizations.slice().sort((a, b) => {
                const aOrgName = a.name[localization.locale] || a.name.fi_FI || a.name.sv_SE || a.name.en_GB;
                const bOrgName = b.name[localization.locale] || b.name.fi_FI || b.name.sv_SE || b.name.en_GB;
                return aOrgName < bOrgName ? -1 : 1;
            });
            const documentsOfOrganizations = organizationsInAlphabeticalOrder.map((org) => {
                const docOfOrg = documents.find((doc) => doc.organization.id === org.id);
                return docOfOrg || null;
            });
            setDocs(documentsOfOrganizations);
            setOrgs(organizationsInAlphabeticalOrder);
        } catch (error) {
            snackbar.showError(error.data?.code);
        } finally {
            loadingIndicator.hide();
        }
    }

    const { translate } = localization;
    const { isRegional } = document.currentDocument.organization;

    return (
        <Dialog open={props.isOpen} onClose={props.close} maxWidth={'md'} fullWidth>
            <DialogTitle>
                {translate(
                    props.type === 'LARGE_REPORT'
                        ? isRegional
                            ? 'COUNTIES_LARGE_TITLE'
                            : 'REGIONAL_LARGE_TITLE'
                        : isRegional
                        ? 'COUNTIES_YEARLY_TITLE'
                        : 'REGIONAL_YEARLY_TITLE',
                )}
                <Tooltip
                    title={translate(isRegional ? 'REGIONAL_TITLE_TIP' : 'COUNTIES_TITLE_TIP')}
                    placement="right-start"
                >
                    <InfoIcon
                        color="primary"
                        style={{
                            marginLeft: theming.spacing(1),
                            verticalAlign: 'middle',
                            paddingBottom: '3px',
                        }}
                    />
                </Tooltip>
            </DialogTitle>
            <DialogContent dividers>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ borderBottom: '0px', padding: 2 }}>
                                <Typography>
                                    <b>{translate(isRegional ? 'ORGANIZATION' : 'REGIONAL')}</b>
                                </Typography>
                            </TableCell>
                            <TableCell style={{ borderBottom: '0px', padding: 2 }}>
                                <Typography>
                                    <b>{translate('DOCUMENTS_NAME')}</b>
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orgs.map((org, i) => (
                            <TableRow key={i} hover style={{ cursor: docs[i]?.name ? 'pointer' : 'default' }}>
                                <TableCell style={{ borderBottom: '0px', padding: 1 }}>
                                    <Typography>
                                        {org.name[localization.locale] ||
                                            org.name.fi_FI ||
                                            org.name.sv_SE ||
                                            org.name.en_GB}
                                    </Typography>
                                </TableCell>
                                <TableCell style={{ borderBottom: '0px', padding: 1 }}>
                                    {docs[i]?.id && docs[i]?.name ? (
                                        <a href={`/preview/${docs[i]?.id}`} rel="noopener noreferrer" target="_blank">
                                            <Typography>{docs[i]?.name}</Typography>
                                        </a>
                                    ) : (
                                        <Typography style={{ color: theming.shvkMediumGrey }}>
                                            <i>{translate('OFFICIAL_DOCUMENT_NOT_FOUND')}</i>
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={props.close}>
                    {translate('CLOSE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default observer(CrossOrganizationDialog);
